<template>
<v-dialog
    v-model="dialog"
    max-width="600"
    v-if="profile != null"
>
    <v-card>
        <div
            v-if="is_complete"
            ref="confettiContainer"
            class="confetti-container"
        ></div>

        <v-card-title>{{profile.internal_stable_label}}</v-card-title>

        <v-card-text
            v-if="process_id == null"
        >
            <v-select
                label="Industry"
                :items="industry_choices"
                v-model="industry"
            />

            <div
                v-if="!is_owned_by_current_user"
            >
                <b>WARNING:</b> This demo account is not owned by you. Type <code>confirm</code> if you are sure you want to reset another user's demo account.

                <v-text-field
                    label="Confirm"
                    v-model="confirm_text"
                    hint="type 'confirm'"
                    @keyup.enter="onOk"
                />
            </div>
        </v-card-text>

        <v-card-text
            v-if="demo_builder_progress != null"
        >
            <v-checkbox
                v-for="(item, index) in demo_builder_progress.steps"
                :key="index"
                :label="item.text"
                :input-value="item.value"
                style="margin-bottom: -40px"
                disabled
                readonly
            />

            <div
                style="height: 25px"
            >
            </div>
        </v-card-text>

        <v-card-actions
            v-if="process_id == null"
        >
            <v-spacer></v-spacer>

            <v-btn
                text
                @click="onCancel"
            >
            Cancel
            </v-btn>

            <v-btn
                color="primary darken-1"
                text
                @click="onOk"
                :disabled="!is_valid"
            >
            Reset Demo
            </v-btn>

        </v-card-actions>

        <v-card-actions
            v-if="process_id != null"
            style="min-height: 50px;"
        >
           <v-spacer></v-spacer>

            <v-progress-circular
                v-if="!is_complete"
                :size="30"
                :width="3"
                color="primary"
                indeterminate
                style="margin-bottom: 7px"
                >
            </v-progress-circular>
            
            <div
                class="d-flex align-center"
                style="margin-bottom: 7px"
                v-if="is_complete"
            >
                <v-icon
                    color="primary"
                    size="36"
                    class="dancing-bird"
                >
                    mdi-bird
                </v-icon>
                <span class="font-weight-bold primary--text text-h6">
                    Success!
                </span>
                <v-icon
                    color="primary"
                    size="36"
                    class="dancing-bird"
                >
                    mdi-bird
                </v-icon>
            </div>
           
           <v-spacer></v-spacer>
        </v-card-actions>

    </v-card>
</v-dialog>
</template>

<script>
// No library import needed!

export default {
    data() {
        return {
            dialog: false,
            profile: null,
            industry: null,
            confirm_text: null,
            process_id: null,
            demo_builder_progress: null,
            is_complete: false,
        }
    },

    methods: {
        showDialog(profile) {
            this.process_id = null;
            this.demo_builder_progress = null;
            this.profile = profile;
            this.industry = null;
            this.confirm_text = null;
            this.is_complete = false;
            this.dialog = true;
        },

        onOk() {
            if (!this.is_valid) return;
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'RESET: /api/v1/admin/demo/reset',
                'params': {
                    'organization_id': this.profile.organization_id,
                    'industry': this.industry,
                },
                'on_success': (data) => {
                    this.process_id = data.process_id;
                    // Wait a bit to let the process start before polling
                    window.setTimeout(() => { this.checkProgress(); }, 2000);
                },
                'on_error': 'Unable to reset demo data',
            });
        },

        checkProgress() {
            if (this.process_id == null)
                return;
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/process/:process_id',
                'params': {
                    'process_id': this.process_id,
                },
                'on_success': (data) => {
                    this.demo_builder_progress = data.status;

                    if (this.demo_builder_progress && this.demo_builder_progress.complete) {
                        this.is_complete = true;

                        // Use $nextTick to ensure the confetti container is in the DOM
                        this.$nextTick(() => {
                            this.launchConfetti();
                        });

                        window.setTimeout(() => {
                            this.$emit('reset')
                            this.dialog = false;
                            this.$rc.snackbar('Demo setup complete', 1500);
                        }, 4000); // 4-second delay bc we have to have fun first
                        return;
                    }
                    if (data.exception) {
                        this.$rc.alert('Demo setup failed, try running again', data.exception);
                        this.dialog = false;
                        return;
                    }
                    window.setTimeout(() => { this.checkProgress(); }, 1000);
                },
                'on_error': () => {},
            });
        },

        launchConfetti() {
            const container = this.$refs.confettiContainer;
            if (!container) return;

            const PARTICLE_COUNT = 80;
            const COLORS = ['#FFD700', '#FF69B4', '#00BFFF', '#ADFF2F', '#FF4500', '#BA55D3'];

            for (let i = 0; i < PARTICLE_COUNT; i++) {
                const particle = document.createElement('div');
                container.appendChild(particle);
                
                // Style the particle
                const color = COLORS[Math.floor(Math.random() * COLORS.length)];
                const size = Math.floor(Math.random() * 5) + 8; // size between 8px and 13px
                particle.style.width = `${size}px`;
                particle.style.height = `${size}px`;
                particle.style.backgroundColor = color;
                particle.style.position = 'absolute';
                particle.style.left = '50%';
                particle.style.top = '50%';
                particle.style.opacity = '1';
                // Add the transition after initial styles are set
                particle.style.transition = 'all 2s ease-out';
                
                // Calculate random trajectory
                const angle = Math.random() * 360;
                const distance = Math.random() * 150 + 200; // travel distance
                const rotation = Math.random() * 720 - 360; // random rotation
                const x = Math.cos(angle * (Math.PI / 180)) * distance;
                const y = Math.sin(angle * (Math.PI / 180)) * distance;

                // Animate the particle
                requestAnimationFrame(() => {
                    particle.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${rotation}deg)`;
                    particle.style.opacity = '0';
                });

                // Remove the particle from the DOM after the animation
                setTimeout(() => {
                    if (particle.parentNode === container) {
                       container.removeChild(particle);
                    }
                }, 2000);
            }
        },

        onCancel() {
            this.dialog = false;
        },
    },

    computed: {
        industry_choices() {
            return [{
                'text': 'Wine',
                'value': 'wine'
            }, ];
        },
        is_valid() {
            if (this.industry == null) return false;
            if (!this.is_owned_by_current_user && ('' + this.confirm_text).toLowerCase().trim() != 'confirm') return false;
            return true;
        },
        is_owned_by_current_user() {
            if (this.profile == null) return false;
            return this.profile.redchirp_owner_employee_id == this.$rc.$state.getEmployeeId();
        },
    },
}
</script>

<style lang="scss" scoped>
/* Animation for the bird icon */
.dancing-bird {
  animation: dance-animation 0.8s infinite ease-in-out;
}

@keyframes dance-animation {
  0%, 100% {
    transform: translateY(0) rotate(0);
  }
  25% {
    transform: translateY(-5px) rotate(-5deg);
  }
  75% {
    transform: translateY(-5px) rotate(5deg);
  }
}

/* Styles for the confetti container */
.confetti-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 10;
    pointer-events: none; /* Allows clicks to pass through */
}
</style>